import { useEffect, useState } from 'react';
import { Grid, Typography, Box, Chip, makeStyles } from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Progress, TrendLine, WarningPanel } from '@backstage/core-components';

const ANNOTATION = 'dynatrace.com/dynatrace-entity-id';

type MetricDef = {
  key: string;
  title: string;
  selector: string;
  unit: 'ms' | '%' | 'count' | 'us' | 'us-per-req';
  color: string;
};

const METRICS: MetricDef[] = [
  {
    key: 'responseTime',
    title: 'P90 Response Time',
    selector: 'builtin:service.response.time:percentile(90)',
    unit: 'ms',
    color: '#00e5ff',
  },
  {
    key: 'failureRate',
    title: 'Failure Rate',
    selector: 'builtin:service.errors.total.rate',
    unit: '%',
    color: '#ff1744',
  },
  {
    key: 'throughput',
    title: 'Throughput',
    selector: 'builtin:service.requestCount.total',
    unit: 'count',
    color: '#00e676',
  },
  {
    key: 'cpuPerRequest',
    title: 'CPU per Request',
    selector: 'builtin:service.cpu.perRequest',
    unit: 'us-per-req',
    color: '#ffea00',
  },
  {
    key: 'cpuTime',
    title: 'CPU Time',
    selector: 'builtin:service.cpu.time',
    unit: 'us',
    color: '#ff6d00',
  },
  {
    key: 'dbFailureRate',
    title: 'DB Connection Failure Rate',
    selector: 'builtin:service.dbconnections.failureRate',
    unit: '%',
    color: '#d500f9',
  },
];

type SeriesData = {
  values: number[];
  current: number | null;
};

type DynatraceMetricsResponse = {
  result?: Array<{
    data?: Array<{
      values: Array<number | null>;
      timestamps: number[];
    }>;
  }>;
};

const useStyles = makeStyles(theme => ({
  metricBox: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  metricTitle: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  metricValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
  },
  noData: {
    fontSize: '0.85rem',
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  prodChip: {
    backgroundColor: '#d32f2f',
    color: '#fff',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  headerWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
}));

const formatValue = (value: number | null, unit: MetricDef['unit']): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return 'n/a';
  switch (unit) {
    case 'ms':
      // Dynatrace returns microseconds for response.time; convert to ms
      return `${(value / 1000).toFixed(1)} ms`;
    case '%':
      return `${value.toFixed(2)} %`;
    case 'count':
      return value.toLocaleString();
    case 'us':
      // Show as ms if >1000us for readability
      return value > 1000 ? `${(value / 1000).toFixed(1)} ms` : `${value.toFixed(0)} µs`;
    case 'us-per-req':
      return `${value.toFixed(0)} µs/req`;
    default:
      return String(value);
  }
};

export const DynatraceMetricsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const entityId = entity.metadata.annotations?.[ANNOTATION];
  const [series, setSeries] = useState<Record<string, SeriesData>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (entityId) {
      const load = async () => {
        setLoading(true);
        setError(null);
        try {
          const proxyBase = await discoveryApi.getBaseUrl('proxy');
          const entitySelector = `type(SERVICE),entityId(${entityId})`;

          const results = await Promise.all(
            METRICS.map(async m => {
              const url =
                `${proxyBase}/dynatrace/metrics/query` +
                `?metricSelector=${encodeURIComponent(m.selector)}` +
                `&entitySelector=${encodeURIComponent(entitySelector)}` +
                `&from=now-1h` +
                `&resolution=1m`;
              const res = await fetchApi.fetch(url);
              if (!res.ok) {
                throw new Error(`${m.title}: HTTP ${res.status}`);
              }
              const json: DynatraceMetricsResponse = await res.json();
              const data = json.result?.[0]?.data?.[0];
              const values = (data?.values ?? []).map(v => (v === null ? 0 : v));
              const lastValid = [...(data?.values ?? [])]
                .reverse()
                .find(v => v !== null && v !== undefined);
              return [m.key, { values, current: lastValid ?? null }] as const;
            }),
          );

          if (!cancelled) {
            setSeries(Object.fromEntries(results));
          }
        } catch (e: any) {
          if (!cancelled) setError(e.message || 'Failed to load Dynatrace metrics');
        } finally {
          if (!cancelled) setLoading(false);
        }
      };

      load();
    }

    return () => {
      cancelled = true;
    };
  }, [entityId, discoveryApi, fetchApi]);

  if (!entityId) {
    return null;
  }

  return (
    <InfoCard
      title={
        <Box className={classes.headerWrap}>
          <span>Service Metrics</span>
          <Chip size="small" label="PRODUCTION" className={classes.prodChip} />
        </Box>
      }
      subheader={`Last hour · ${entityId}`}
    >
      {loading && <Progress />}
      {error && <WarningPanel title="Could not load metrics" message={error} />}
      {!loading && !error && (
        <Grid container spacing={2}>
          {METRICS.map(m => {
            const s = series[m.key];
            const hasData = s && s.values.length > 0 && s.values.some(v => v > 0);
            return (
              <Grid item xs={12} sm={6} md={4} key={m.key}>
                <Box className={classes.metricBox}>
                  <Typography className={classes.metricTitle}>{m.title}</Typography>
                  <Typography className={classes.metricValue}>
                    {formatValue(s?.current ?? null, m.unit)}
                  </Typography>
                  {hasData ? (
                    <TrendLine
                      data={s!.values}
                      color={m.color}
                      title={m.title}
                    />
                  ) : (
                    <Typography className={classes.noData}>No data in last 2h</Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </InfoCard>
  );
};
